import { Divider, Drawer, IconButton, Button } from "@mui/material";
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react'
import Split from "react-split";
import CloseIcon from "@mui/icons-material/Close";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { GraphiQL } from "graphiql";
import "graphiql/graphiql.css";
import { explorerPlugin } from "@graphiql/plugin-explorer";
import { treeActions } from "../../../../redux/slices/treeSlice";
import { useDispatch, useSelector } from "react-redux";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep } from "lodash";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";

const explorer = explorerPlugin({
  showAttribution: false,
});

const createGraphQLFetcher = (url) => async (graphQLParams) => {
  if (!url) {
    return {
      errors: [{ message: "GraphQL endpoint URL is not configured" }],
    };
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(graphQLParams),
    });

    if (!response.ok) {
      let errorMessage = `Network error: ${response.status}`;
      try {
        const text = await response.text();
        if (text) {
          errorMessage = `${errorMessage} - ${text}`;
        }
      } catch {
        // If reading response text fails, use the default error message
      }
      
      console.error("GraphQL fetch error:", response.status, errorMessage);
      return {
        errors: [{ message: errorMessage }],
      };
    }

    const data = await response.json();
    return data;
  } catch (err) {
    const errorMessage = err.message || String(err);
    console.error("GraphQL fetch exception:", err);
    return {
      errors: [{ message: `Request failed: ${errorMessage}` }],
    };
  }
};

function GraphQLQueryBuilder({ open, onClose }) {
  const { t } = useTranslation();

  

  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  const dispatch = useDispatch();
  const url = selectedNode?.URL || "";
  const [query, setQuery] = useState(() => {
    return selectedNode?.FIELD?.VALUE || "";
  });
  
  useEffect(() => {
    if (open && selectedNode?.FIELD?.VALUE) {
      setQuery(selectedNode.FIELD.VALUE);
    }
  }, [open, selectedNode]);
  
  useEffect(() => {
    if (!open) return;
  
    const openExplorer = () => {
      // GraphiQL Explorer button
      const explorerBtn = document.querySelector(
        'button[aria-label="Show GraphiQL Explorer"]'
      );
  
      if (explorerBtn) {
        explorerBtn.click(); 
        return true;
      }
      return false;
    };
  
    
    if (openExplorer()) return;
  
    
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (openExplorer() || attempts > 10) {
        clearInterval(interval);
      }
    }, 50);
  
    return () => clearInterval(interval);
  }, [open]);
  
  const graphQLFetcher = useCallback(createGraphQLFetcher(url), [url]);
  
  // Memoize plugins array to prevent GraphiQL from reinitializing the explorer plugin
  const plugins = useMemo(() => [explorer], []);
  
  // Memoize onEditQuery callback to maintain stable reference
  const handleEditQuery = useCallback((newQuery) => {
    setQuery(newQuery);
  }, []);

  const handleApply = () => {
    if (query) {
      const value = query;
      const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
        if (obj?.key !== selectedNode.key) return;
        obj.FIELD.VALUE = value;
        dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: rightTree.currentTab,
            data: obj,
          })
        );
      });
      dispatch(
        treeActions.setNewRightTreeTranslatedData({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: updatedData,
        })
      );
      onClose();
    }
  };
  return (
    <>
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: "100%",
            // height: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
            background: "transparent",
          },
        }}>
        <Split
          className="split height-100 flex"
          direction="horizontal"
          gutterSize={4}
          snapOffset={0}
          dragInterval={4}
          minSize={[550, 0]}
          sizes={[67, 33]}>
          <div className="height-100" style={{ background: "#ffffff" }}>
            {/* GraphQL Query Builder Component */}
            <div className="quick-add-container" key={0}>
              <div style={{ lineHeight: "25px" }}>
                <p style={{ marginBottom: "10px" }}>{t("LBL_QUERY_BUILDER")}</p>
                <div className="font-12">
                  <p>{t("QUERY_BUILDER_DESCRIPTION")}</p>
                </div>
              </div>

              <IconButton onClick={onClose} sx={{ borderRadius: "5px" }}>
                <CloseIcon
                  sx={{
                    height: "28px",
                    width: "28px",
                    color: "#777777",
                  }}
                />
              </IconButton>
            </div>
            <div style={{ height: "80%" }}>
              {/* <GraphQLBuilderPanel /> */}
              <div
                className="graphql-builder-container"
                style={{ height: "100%" }}>
                <GraphiQL
                  key={`graphiql-${url}-${open}`}
                  query={query}
                  fetcher={graphQLFetcher}
                  plugins={plugins}
                  onEditQuery={handleEditQuery}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                borderTop: "1px solid #e0e0e0",
              }}>
              <Button
                variant="outlined"
                onClick={handleApply}
                style={{ margin: "10px", marginTop: "15px" }}>
                {t("APPLY")}
              </Button>
              <Button
                variant="outlined"
                onClick={onClose}
                style={{ margin: "10px", marginTop: "15px" }}>
                {t("CANCEL")}
              </Button>
            </div>
          </div>
          <div>{""}</div>
        </Split>
      </Drawer>
    </>
  );
}

export default GraphQLQueryBuilder;
